import { GoogleIcon } from "Icons/Google";

import { useEffect, useRef, useState } from "react";
import Turnstile from "react-turnstile";

import { css } from "@emotion/react";

import { useRouter } from "next/router";

import { Button } from "components/shared/library/Button";
import { ButtonLink } from "components/shared/library/ButtonLink";
import { Input } from "components/shared/library/Input";
import { Typography } from "components/shared/library/Typography";
import { BrandSquares } from "components/shared/onboarding/BrandSquares";
import { OnboardingContainer } from "components/shared/onboarding/OnboardingContainer";
import { TwoPanelCard } from "components/shared/onboarding/TwoPanelCard";

import { useAlert } from "contexts/alertContext";
import { useAuth } from "contexts/authContext";

import { signInWithGoogle, signUp } from "apis/authentication";

import { isWebview } from "utils/device";
import { down } from "utils/mediaQueryStrings";
import { getExternalPath, getOnboardingUrl, getSignInUrl } from "utils/urls";
import {
  cleanName,
  emailValidator,
  passwordValidator,
  useEmailValidation,
  usePasswordValidation,
} from "utils/validator";

import { uiGray } from "constants/colors";

const style = {
  content: css({
    display: "flex",
    gap: 48,
    height: "100%",
    flexDirection: "column",
    [down("mobile")]: { justifyContent: "space-between" },
  }),
  rightPanelTop: css({
    display: "flex",
    flexDirection: "column",
    gap: 48,
  }),
  bottomRightPanel: css({
    display: "flex",
    flexDirection: "column",
    gap: 16,
    alignItems: "center",
  }),
  hideOnMobile: css({ [down("mobile")]: { display: "none" } }),
  email: css({
    display: "grid",
    gridTemplateRows: "auto auto",
    gap: 10,
    marginBottom: 24,
  }),
  password: css({
    display: "grid",
    gridTemplateRows: "auto auto",
    gap: 10,
  }),
  accountExist: css({
    display: "flex",
    alignItems: "center",
    gap: 4,
  }),
  name: css({
    gap: 16,
    display: "flex",
    [down("mobile")]: { display: "grid" },
  }),
  fields: css({
    display: "flex",
    gap: 16,
    flexDirection: "column",
  }),
};

type Props = {
  setEmailForResend: (email: string) => void;
  title?: string;
  subtitle?: string;
  redirectTo?: string;
  email?: string;
};

export const SignUpForm = ({
  setEmailForResend,
  title = "Land your dream job with Ribbon",
  subtitle = "Create a free account and kickstart your career.",
  redirectTo,
  email: initialEmail,
}: Props) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const [captchaToken, setCaptchaToken] = useState("");

  const router = useRouter();
  const { user } = useAuth();
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const [email, setEmail] = useState(initialEmail || "");

  const [password, setPassword] = useState("");
  const [showSignUpWithGoogle, setShowSignUpWithGoogle] = useState(true);

  const isEmailError = useEmailValidation(email);
  const isPasswordError = usePasswordValidation(password);

  const { createAlert } = useAlert();

  useEffect(() => {
    if (isWebview()) setShowSignUpWithGoogle(false);
  }, []);

  const handleSignUp = async () => {
    if (
      cleanName(firstName) &&
      cleanName(lastName) &&
      emailValidator(email) &&
      passwordValidator(password)
    ) {
      const { error } = await signUp({
        email,
        password,
        firstName: cleanName(firstName),
        lastName: cleanName(lastName),
        currentPath: router.asPath,
        onSuccessfulAuthRedirect: redirectTo,
        captchaToken,
      });

      if (error) {
        // Handle both string errors and object errors from Supabase
        if (typeof error === "object" && error !== null) {
          // For structured errors from Supabase
          if (error.code === "invalid_credentials") {
            createAlert(
              "error",
              "Invalid login credentials. Please check your email and password."
            );
          } else if (error.code === "user_already_exists") {
            router.push(getSignInUrl({ currentPath: router.asPath, email }));
            createAlert(
              "error",
              "This email is already registered. Please sign in instead."
            );
          } else if (error.message) {
            createAlert("error", error.message);
          } else {
            createAlert(
              "error",
              "There was an error signing up. Please try again."
            );
          }
        } else if (typeof error === "string") {
          // For string errors
          createAlert("error", error);

          // If it's a user already exists error, redirect to sign in
          if (error.includes("Please sign in with your existing account")) {
            router.push(getSignInUrl({ currentPath: router.asPath, email }));
          }
        } else {
          // Fallback for any other error type
          createAlert(
            "error",
            "There was an error signing up. Please try again."
          );
        }

        return false; // we do not want onSuccess to trigger if there is an error
      }

      setEmailForResend(email);
      return true; // we want onSuccess to trigger
    }
  };

  return (
    <OnboardingContainer>
      <TwoPanelCard
        title={title}
        content={<BrandSquares customCss={style.hideOnMobile} />}
        subtitle={subtitle}
      >
        <div css={style.content}>
          <div css={style.rightPanelTop}>
            <div css={style.fields}>
              <div css={style.name}>
                <Input
                  displayLabel
                  placeholder="First name"
                  label="First name"
                  onChange={setFirstName}
                  value={firstName}
                />
                <Input
                  displayLabel
                  placeholder="Last name"
                  label="Last name"
                  onChange={setLastName}
                  value={lastName}
                />
              </div>
              <Input
                displayLabel
                isError={isEmailError}
                placeholder="Enter email"
                label="Email"
                onChange={setEmail}
                value={email}
                helpText={
                  isEmailError ? "Please enter a valid email address" : ""
                }
              />

              <Input
                displayLabel
                type="password"
                isError={isPasswordError}
                placeholder="Enter password"
                label="Password"
                onEnter={() => submitButtonRef.current?.click()}
                onChange={setPassword}
                value={password}
                helpText={
                  isPasswordError
                    ? "Please enter a valid password (at least 6 characters)"
                    : ""
                }
              />
            </div>
          </div>
          <div css={style.bottomRightPanel}>
            <Turnstile
              appearance="always"
              theme="light"
              sitekey={process.env.NEXT_PUBLIC_CLOUDFLARE_SITE_KEY || ""}
              onSuccess={(token) => {
                setCaptchaToken(token);
              }}
              onError={() => {
                createAlert(
                  "error",
                  "Captcha failed. Please reload the page and try again."
                );
              }}
            />
            <Button
              onClick={handleSignUp}
              disabled={
                !passwordValidator(password) ||
                !emailValidator(email) ||
                !!user?.id ||
                !cleanName(firstName) ||
                !cleanName(lastName) ||
                !captchaToken
              }
              isFullWidth
              buttonRef={submitButtonRef}
            >
              Create an account
            </Button>
            {showSignUpWithGoogle && (
              <Button
                IconLeft={GoogleIcon}
                background="outlined"
                color="black"
                onClick={async () => {
                  // redirect back to onboarding after sign in
                  const { error } = await signInWithGoogle(
                    (redirectTo && getExternalPath(redirectTo)) ||
                      getExternalPath(
                        getOnboardingUrl({ currentPath: router.pathname })
                      )
                  );

                  if (error) {
                    // Display error to user
                    if (typeof error === "object" && error !== null) {
                      createAlert(
                        "error",
                        error.message ||
                          "There was an error signing up with Google. Please try again."
                      );
                    } else if (typeof error === "string") {
                      createAlert("error", error);
                    } else {
                      createAlert(
                        "error",
                        "There was an error signing up with Google. Please try again."
                      );
                    }
                  }

                  return !error;
                }}
                isFullWidth
                disabled={!!user?.id || !captchaToken}
              >
                Sign up with Google
              </Button>
            )}
            <div css={style.accountExist}>
              <Typography color={uiGray[80]}>{`Have an account?`}</Typography>
              <ButtonLink
                href={getSignInUrl({ currentPath: router.asPath })}
                background="link"
                sameTab
              >
                Sign in
              </ButtonLink>
            </div>
          </div>
        </div>
      </TwoPanelCard>
    </OnboardingContainer>
  );
};
